/**
 * インボイス経過措置・控除対象額（消費税概算）のプレビュー計算。
 *
 * 確定値はバックエンド（yonarezi app/services/expenses/tax_snapshot.rb）が
 * 経費の保存時にスナップショットとして計算・永続化する。
 * この関数群は未保存行のプレビュー表示専用であり、計算式は必ずバックエンドと一致させること。
 * 端数はすべて切り捨て。
 */
export default function useInvoiceDeduction() {
  // 経過措置: 対象日が期限日以前なら対応する控除率(%)。それ以降は0%
  const TRANSITIONAL_RATE_STEPS = [
    { deadline: '2026-09-30', rate: 80 },
    { deadline: '2029-09-30', rate: 50 },
  ]

  /**
   * 対象日における未登録事業者の経過措置控除率(%)
   * @param {string} date - 'YYYY-MM-DD'
   * @returns {number|null} 80/50/0。dateがない場合はnull
   */
  const transitionalRate = date => {
    if (!date) return null

    const step = TRANSITIONAL_RATE_STEPS.find(s => date <= s.deadline)

    return step ? step.rate : 0
  }

  /**
   * 対象日の経過措置の説明文（フッター表示用）
   */
  const transitionalRateText = date => {
    if (!date) return ''
    if (date <= '2026-09-30') return '2026-09-30以前 → 経過措置 80%'
    if (date <= '2029-09-30') return '2026-10-01〜2029-09-30 → 経過措置 50%'

    return '2029-10-01以降 → 0%（控除なし）'
  }

  /**
   * 控除率(%)を判定する
   * @param {string} taxRate - 'standard' | 'reduced' | 'exempt' | 'blank'
   * @param {string|null} invoiceCategory - 'qualified' | 'unregistered' | null(取引先未選択=適格扱い)
   * @param {string} date - 対象日 'YYYY-MM-DD'
   * @returns {number|null} 100/80/50/0。非課税・税率未選択は対象外(null)
   */
  const deductionRatePercent = (taxRate, invoiceCategory, date) => {
    if (!taxRate || taxRate === 'blank' || taxRate === 'exempt') return null
    if (!invoiceCategory || invoiceCategory === 'qualified') return 100

    return transitionalRate(date)
  }

  /**
   * 控除対象額を計算する
   * @param {number|null} taxAmount - 税額（useTaxCalculationのcalculateTaxAmount結果）
   * @returns {number|null} 控除対象額。対象外はnull
   */
  const calculateDeductibleTaxAmount = (taxAmount, taxRate, invoiceCategory, date) => {
    const rate = deductionRatePercent(taxRate, invoiceCategory, date)
    if (rate === null || taxAmount === null || taxAmount === undefined) return null

    return Math.floor((taxAmount * rate) / 100)
  }

  return {
    transitionalRate,
    transitionalRateText,
    deductionRatePercent,
    calculateDeductibleTaxAmount,
  }
}
